export interface FAQItem {
  question: string;
  answer: string;
}

export interface FAQSection {
  sectionTitle: string;
  items: FAQItem[];
}

export const faqSections: FAQSection[] = [
  {
    sectionTitle: "Getting Started",
    items: [
      {
        question: "How do I sign up?",
        answer: "Simply choose a plan (Starter, Growth, or Portfolio) by clicking the Upgrade button and complete your subscription. You'll receive immediate access to your dashboard, where you can submit your trademark information and begin the application process.",
      },
      {
        question: "What's included in each plan?",
        answer: "- **Starter ($599/year or $69/month):** 1 US trademark filing per year, basic search, status tracking, email support\n- **Growth ($1,199/year or $129/month):** 2 US trademark filings per year, comprehensive search, priority support, 1 strategy call, office-action help\n- **Portfolio ($1,799/year or $189/month):** 3 US trademark filings per year, expanded search, quarterly monitoring, 2 strategy calls, discounted maintenance filing",
      },
      {
        question: "Can I cancel anytime?",
        answer: "Yes. Monthly subscribers can cancel anytime with no penalty. Annual subscribers can cancel at the next renewal (no refunds of already-spent fees or pre-used filings). We recommend canceling via your account dashboard.",
      },
      {
        question: "Can I change plans?",
        answer: "Yes. You can upgrade to a higher tier anytime (pro-rated or restarted annual term). Downgrades take effect at your next renewal. Contact support for details on your specific situation.",
      },
    ],
  },
  {
    sectionTitle: "Pricing & Billing",
    items: [
      {
        question: "Are government fees included in the plan price?",
        answer: "Yes. All plan prices include USPTO filing fees for the included trademark applications. However, additional classes beyond the first one, and extra filings, incur additional government fees ($350 per class).",
      },
      {
        question: "What payment methods do you accept?",
        answer: "We accept all major credit cards and payment methods supported by our Wix payment system (Visa, Mastercard, American Express, PayPal, etc.). Payments are processed securely.",
      },
      {
        question: "What if I upgrade mid-year?",
        answer: "When upgrading, your new plan takes effect immediately. Billing details will be adjusted on your next renewal cycle. For precise pro-rating, contact support.",
      },
      {
        question: "Do unused filings roll over to the next year?",
        answer: "No. Unused filings do not roll over. Each subscription year resets your filing allowance. Plan ahead and contact us if you need more filings than your plan includes.",
      },
      {
        question: "Can I get a refund if I don't use my included filings?",
        answer: "Subscriptions are generally non-refundable after the first 7 days. If you cancel within 7 days of purchase without using any service, contact us for a refund request. Otherwise, your subscription fee is non-refundable even if you don't use all included filings.",
      },
    ],
  },
  {
    sectionTitle: "Trademark Applications",
    items: [
      {
        question: "What information do I need to provide?",
        answer: "- Your full name/business name and address\n- The trademark mark (word, logo, or both)\n- Goods/services you plan to use it for\n- Whether you're already using the mark in commerce (US applicants)\n- For non-US applicants: existing foreign registration/application details or proof of intent to use\n\nSee our \"Application Checklist\" for the complete list.",
      },
      {
        question: "Can non-US applicants file with you?",
        answer: "Yes. We serve non-US customers, but they must be represented by a US-licensed attorney. Our team handles this representation. Non-US customers may need to provide proof of a foreign registration or foreign application (Section 44 basis) or evidence of intent to use.",
      },
      {
        question: "How long does trademark registration take?",
        answer: "The full trademark examination process typically takes **6–12 months** from filing date. Initial approval may take 2–4 months. If the USPTO issues an Office Action (request for changes), responses typically add 1–3 months. We'll keep you updated via your dashboard and email.",
      },
      {
        question: "What if the USPTO rejects my application?",
        answer: "The USPTO may issue an \"Office Action\" asking for changes or raising objections. Most Starter plans include help with **non-substantive responses** (e.g., simple ID/specimen fixes). Growth and Portfolio plans include more office-action support. Substantive refusals (e.g., likelihood of confusion with another mark) may require additional fees or legal strategy consultation.",
      },
      {
        question: "Do you offer a trademark search before filing?",
        answer: "Yes. All plans include a search:\n- **Starter:** Basic knockout search (USPTO federal + limited common law check)\n- **Growth & Portfolio:** Comprehensive search (USPTO + broader common law + state checks)\n\nThe search helps identify potential conflicts before you invest in filing. However, a search is not a guarantee of approval.",
      },
      {
        question: "Can I file multiple classes in one application?",
        answer: "Yes. Your plan's included filing can cover multiple classes (e.g., goods and services), but the government fee is per class. Each additional class beyond the first incurs an extra $350 government fee + $199 service fee.",
      },
      {
        question: "What's the difference between \"Intent to Use\" and \"Use in Commerce\"?",
        answer: "- **Use in Commerce (Section 1(a)):** You're already using the mark in the US in commerce. You provide proof (specimens).\n- **Intent to Use (Section 1(b)):** You plan to use it soon but aren't using it yet. You submit proof of use later (within 6 months of approval).\n\nFor non-US applicants using Section 44 basis, you can rely on a foreign registration and defer US use proof.",
      },
    ],
  },
  {
    sectionTitle: "Support & Service Levels",
    items: [
      {
        question: "How do I contact support?",
        answer: "Log into your dashboard and email us, or use our in-app messaging system. Starter plan gets standard email support (3–5 business days). Growth and Portfolio get priority support (1–2 business days).",
      },
      {
        question: "Can I schedule a call with someone?",
        answer: "Growth and Portfolio plans include 1–2 strategy calls per year with our team. Starter plan gets email support only, but you can purchase additional calls as an add-on ($99 each, 30 minutes).",
      },
      {
        question: "What happens if I have an Office Action I can't handle?",
        answer: "- **Starter:** You can purchase help ($249 for a substantive response)\n- **Growth:** Non-substantive responses included; substantive responses start at $249\n- **Portfolio:** Includes non-substantive responses; substantive responses negotiated\n\nIf your mark faces a serious refusal, we'll advise you on options (amend, narrow the mark, appeal, or abandon).",
      },
    ],
  },
  {
    sectionTitle: "Maintenance & Renewal",
    items: [
      {
        question: "What happens after my trademark is registered?",
        answer: "Congratulations! You own a registered trademark. However, you must maintain it by filing:\n- **Section 8 Declaration** (Proof of Use) at **5–6 years** after registration\n- **Section 9 Renewal** (Keep your registration alive) every **10 years**\n\nThe combined fee is typically $650 per class every 10 years.",
      },
      {
        question: "Do your plans include maintenance filings?",
        answer: "- **Starter:** Maintenance filing service is available à la carte ($249 service fee + $325 government fee for Section 8).\n- **Growth & Portfolio:** Discounted maintenance filing service available ($199 service fee + government fees).\n\nWe'll send you reminders well in advance so you don't miss these deadlines.",
      },
      {
        question: "What if I miss a maintenance deadline?",
        answer: "If you miss a Section 8 deadline, your registration can be cancelled. Missing a Section 9 renewal can also result in loss of your mark. We strongly recommend using our reminder service and renewal filing option to avoid this.",
      },
      {
        question: "Can I convert my Intent-to-Use application to regular registration?",
        answer: "Yes. When you begin using the mark in commerce, you file an \"Amendment to Allege Use\" (AAU) or \"Statement of Use\" (SOU) to transition to a live registration. This is $150 per class via the USPTO. We can help coordinate this.",
      },
    ],
  },
  {
    sectionTitle: "Intellectual Property & Legal",
    items: [
      {
        question: "Does trademark registration guarantee I own the mark?",
        answer: "US trademark registration gives you nationwide rights and a strong legal presumption of ownership. However, if someone else used the mark before you (even without registration), they may have rights. That's why a comprehensive search is important. Registration doesn't guarantee no one will challenge you, but it's your strongest protection tool.",
      },
      {
        question: "Can I trademark anything?",
        answer: "No. The USPTO rejects marks that are:\n- Generic (e.g., \"Software\" for software)\n- Merely descriptive (e.g., \"Red Apples\" for apples)\n- Confusingly similar to existing registered marks\n- Offensive or scandalous\n- Deceptive about the goods/services\n\nA good search and thoughtful application help avoid these issues.",
      },
      {
        question: "What if my trademark is challenged or opposed?",
        answer: "During examination, the USPTO may issue an Office Action. During publication (30-day opposition period), third parties can file oppositions. These are more serious than Office Actions. We can advise and help respond, but complex opposition cases may require specialized counsel (not included in standard plans). We'll discuss options and costs.",
      },
      {
        question: "Do I get legal advice?",
        answer: "We provide trademark application support and guidance, but our team includes non-attorney support staff and service coordinators. For legal advice beyond application preparation (e.g., whether a mark infringes, enforcement strategy), we recommend consulting a licensed trademark attorney. Some complex matters may require external legal counsel at additional cost.",
      },
      {
        question: "Can you help with international trademark registration?",
        answer: "We primarily focus on US trademark registration. International filings (via Madrid Protocol or country-specific applications) are available as **add-ons or custom quotes**. Contact us for international registration options and pricing.",
      },
    ],
  },
  {
    sectionTitle: "Account & Technical",
    items: [
      {
        question: "Can I see the status of my application?",
        answer: "Yes. Your dashboard shows real-time status, key dates, and next steps. You'll also receive email updates when major milestones occur (e.g., application filed, Office Action received, registration granted).",
      },
      {
        question: "How do I download my trademark certificate?",
        answer: "Once your trademark is registered, your dashboard will have a downloadable link to your official registration certificate from the USPTO. You can also retrieve it anytime from the USPTO website using your application/registration number.",
      },
      {
        question: "Can I use my trademark while the application is pending?",
        answer: "Yes, but carefully. You can use the ™ symbol (unregistered mark) immediately. You can only use the ® symbol after you receive your official registration certificate. Using ® before registration is illegal and can lead to penalties.",
      },
      {
        question: "Who owns the trademark—me or your company?",
        answer: "**You own the trademark.** We simply help you file and manage the registration process. We have no ownership rights. The trademark is registered in your name (or your business entity's name). You can transfer, license, or abandon it as you see fit.",
      },
      {
        question: "What if I move or change my business address?",
        answer: "For draft trademark, log into your dashboard and update your address. For submitted/registered trademarks, you should file an \"Assignment\" or address change with the USPTO if required. We can guide you or handle this as a paid service.",
      },
    ],
  },
  {
    sectionTitle: "Disputes & Cancellations",
    items: [
      {
        question: "What if I'm unhappy with the service?",
        answer: "We're here to help. Contact support with your concerns. For longer-standing issues, we'll work to resolve them—contact our support team for details.",
      },
      {
        question: "Can someone else challenge or cancel my registered trademark?",
        answer: "Yes. Third parties can file for **cancellation** if they believe your mark was obtained fraudulently, hasn't been used in commerce for 3+ years, or has become generic. We'll advise you on responses if this happens, though defending a cancellation may require additional legal fees.",
      },
      {
        question: "What if I want to abandon my trademark?",
        answer: "You can abandon a registered trademark anytime by filing a notice with the USPTO (minimal cost, ~$100). You don't need to use it forever. If you abandon, others can register and use similar marks.",
      },
      {
        question: "Do you offer refunds for abandoned applications?",
        answer: "No. Once we file your application with the USPTO, the government filing fee and service fee is non-refundable. If you want to abandon the application, you can request that we do so, but you won't recover the fees already paid.",
      },
    ],
  },
  {
    sectionTitle: "Billing Specifics",
    items: [
      {
        question: "How will I be billed?",
        answer: "On the day you subscribe, your first payment is processed. Monthly subscribers are billed on the same day each month. Annual subscribers are billed on the subscription anniversary.",
      },
      {
        question: "Can I pause my subscription?",
        answer: "Not directly. However, you can cancel and re-subscribe later. Contact support if you'd like to discuss options.",
      },
      {
        question: "What if my payment fails?",
        answer: "We'll notify you via email. If payment isn't received within a grace period (typically 5–7 days), your subscription will be suspended. Update your payment method immediately to resume service.",
      },
    ],
  },
  {
    sectionTitle: "Additional Services & Add-Ons",
    items: [
      {
        question: "Can I buy extra filings beyond my plan?",
        answer: "Yes. Extra filings are available à la carte:\n- **Starter:** $299 per additional filing (gov + service included)\n- **Growth:** $329 per additional filing\n- **Portfolio:** $299 per additional filing",
      },
      {
        question: "What's the cost of additional trademark classes?",
        answer: "$199 service fee + $350 government fee per additional class (beyond the first class in your included filing).",
      },
      {
        question: "Can I purchase a standalone search without filing?",
        answer: "Yes. Trademark searches are available as a one-time purchase ($99 for basic, $199 for comprehensive) if you don't want a full filing or subscription.",
      },
      {
        question: "Do you offer office-action response services beyond what's included?",
        answer: "Yes. Substantive office-action responses (e.g., likelihood of confusion refusals) start at $249. Complex matters are quoted individually. Contact support for an estimate.",
      },
      {
        question: "Can I buy renewal/maintenance filing as a standalone service?",
        answer: "Yes. Section 8 Declaration and Section 9 Renewal filings are available separately:\n- Service fee: $199–$249\n- Government fee: $325 per class (Section 8) or $325 per class (Section 9)",
      },
    ],
  },
  {
    sectionTitle: "Compliance & Legal Notices",
    items: [
      {
        question: "Is this legal advice?",
        answer: "Our service includes trademark application preparation and support, but not legal advice. For questions about trademark law, infringement, enforcement, or disputes, consult a licensed trademark attorney. We can recommend specialists if needed.",
      },
      {
        question: "What information is private, and what becomes public?",
        answer: "Trademark applications become public record. Your name, business address, email, and mark description are viewable in the USPTO database. You can request limited disclosure (hiding your mailing address from public view) at the time of filing.",
      },
      {
        question: "Can you help with trademark disputes or litigation?",
        answer: "No. We focus on filing and registration. For disputes, oppositions, or litigation, you'll need a specialized trademark attorney. We can provide referrals to trusted counsel.",
      },
    ],
  },
  {
    sectionTitle: "International Customers",
    items: [
      {
        question: "I'm not a US citizen. Can I register a US trademark?",
        answer: "Yes. Non-US individuals and businesses can register US trademarks. However, you must be represented by a US-licensed attorney. Our team provides this representation, though you'll pay our service fee on top of government fees.",
      },
      {
        question: "What information do non-US applicants need to provide?",
        answer: "In addition to standard information:\n- Your country of domicile/residence\n- Whether you have an existing trademark registration or application in your home country (helpful for Section 44 basis)\n- Proof of intent to use in US commerce, or proof of current use\n- Your passport/business registration for verification",
      },
      {
        question: "What if I already have a trademark registered in my home country?",
        answer: "Excellent. You can use the **Section 44(e) basis**, which allows you to file a US application based on your existing foreign registration. This is often faster and cheaper than filing de novo. Mention your foreign registration during signup.",
      },
      {
        question: "Can you help me register in multiple countries?",
        answer: "We primarily handle US filings. For international filings, we offer Madrid Protocol coordination as a **custom quote/add-on**. This allows you to extend your US registration to other countries. Contact us for details.",
      },
      {
        question: "What currency are your prices in?",
        answer: "All prices are in **US dollars (USD)**. Government fees to the USPTO are also in USD.",
      },
    ],
  },
  {
    sectionTitle: "General Questions",
    items: [
      {
        question: "Why should I use your service vs. filing myself?",
        answer: "- **Expertise:** Our team handles complex forms and government procedures.\n- **Speed:** Professional filing reduces delays and errors.\n- **Included search:** We identify conflicts upfront.\n- **Support:** You get help with office actions and questions.\n- **Predictable cost:** All-in pricing (government fees included) removes surprises.\n- **Tracking:** Dashboard and reminders keep you on schedule.\n\nDIY filing via USPTO's TEAS system is cheaper ($350) but lacks support and search.",
      },
      {
        question: "Why should I use your service vs. traditional attorneys?",
        answer: "- **Cost:** We're significantly cheaper than traditional trademark attorneys ($1,250–$2,000+ flat fee).\n- **Transparency:** All-in pricing with no surprise bills.\n- **Convenience:** Manage everything online, anytime.\n- **Support:** Unlimited email support and strategy calls (Growth/Portfolio).\n\nTraditional attorneys offer deeper legal advice and handling of complex disputes, but at higher cost.",
      },
      {
        question: "What if I have a question not answered here?",
        answer: "Contact our support team via your dashboard, email, or in-app messaging. We're happy to help clarify anything. For legal questions, we'll recommend consulting a licensed trademark attorney.",
      },
    ],
  },
];

